/**
 * ProShip Direct v2 — shape of the store, plus the vocabulary the screens share.
 *
 * Seeded with three own courier accounts so the tables, provenance chips and rule editor have
 * something to show on first load. The plan itself starts INACTIVE on purpose: the activation
 * flow is the first thing to review, and a seeded-active plan would hide it.
 */

/** The five configurations a merchant may supply per own courier account. */
/**
 * The five things a Direct account can supply for itself, in dependency order.
 *
 * `route` is the standalone path each one had before they became tabs of Courier Setup. The
 * routes still resolve — old links and bookmarks land on the right tab — and the value doubles as
 * the tab's id in ?tab=.
 *
 * `claimedByUpload` marks the dimensions where a file IS the claim of ownership: saving one calls
 * setProvided(true), so there is no separate "Provide my own" button to press. Only set it where
 * the page really does that — a drop zone alone is not enough, see Zones below. See SetupPageShell.
 */
export const SETUP_DIMENSIONS = [
  {
    key: 'serviceability',
    /** Column width is tight where all four are shown side by side. */
    short: 'Coverage',
    label: 'Serviceability',
    route: '/serviceability',
    claimedByUpload: true,
    blurb: 'Where this account can reach, per service type and size',
    fallback: 'ProShip’s coverage for this carrier is used',
  },
  {
    key: 'zones',
    /** Column width is tight where all four are shown side by side. */
    short: 'Zones',
    label: 'Zone Mapping',
    route: '/zones',
    /*
      NOT claimedByUpload, though it has a drop zone. Zones is two settings in one: a lane file
      that overrides individual lanes, and the Zone 1–5 descriptions everything else falls back to.
      The file claims neither — it is an override layer, and taking it as ownership of the
      descriptions would silently make them editable off the back of an unrelated upload.

      So the button stays. Without it there is no way to edit the descriptions at all, which is
      exactly what marking this one upload-claimed did.
    */
    blurb: 'Zone 1–5 for your contract, with overrides where the carrier disagrees',
    fallback: 'ProShip’s zone map is used',
  },
  {
    key: 'tat',
    /** Column width is tight where all four are shown side by side. */
    short: 'TAT',
    label: 'TAT',
    route: '/tat',
    blurb: 'Min and max transit days per zone — the EDD you promise buyers',
    fallback: 'ProShip’s committed TAT is used',
  },
  {
    // The one dimension with no ProShip default behind it. ProShip's rates are ProShip's cost,
    // not the merchant's — standing them in for a Direct contract would put a number on the
    // comparison screen that is not the merchant's number, which is worse than no number.
    key: 'rateCard',
    /** Column width is tight where all four are shown side by side. */
    short: 'Rate card',
    label: 'Rate Card',
    route: '/rate-card',
    claimedByUpload: true,
    blurb: 'Zone × weight for your contract, encrypted and used only to price and rank your shipments',
    required: true,
    fallback: 'Not uploaded — Direct cost and the Direct vs ProShip comparison stay blank for this account, and cheapest-first allocation skips it',
  },
];

/**
 * Shipping label configuration.
 *
 * Scoped to DEFAULT plus per-channel overrides, not per courier account. A label is what the
 * merchant's buyer and warehouse see; it belongs to the brand and the sales channel it shipped
 * from, not to whichever carrier happened to win allocation. A marketplace that mandates its own
 * label format is a channel-level fact; Delhivery vs Bluedart is not.
 *
 * Field names follow the admin panel's label customisation, so a merchant and our ops team are
 * naming the same things.
 */
export const PRINTER_TYPES = [
  { value: 'THERMAL', label: 'Thermal printer', blurb: 'Roll labels, no tax block' },
  { value: 'INKJET', label: 'Ink jet / laser', blurb: 'Sheet labels, tax details included' },
];

export const LABEL_SIZES = [
  { value: '4x6', label: '4 × 6 in — most common', printer: 'THERMAL' },
  { value: '4x4', label: '4 × 4 in — compact', printer: 'THERMAL' },
  { value: 'a4-4up', label: 'A4 — 4 labels per sheet', printer: 'INKJET' },
  { value: 'a5', label: 'A5 — single label', printer: 'INKJET' },
];

/** Grouped exactly as the admin panel groups them, so the two screens read alike. */
export const LABEL_FIELD_GROUPS = [
  {
    key: 'order',
    title: 'Order info',
    fields: [
      { key: 'orderId', label: 'Order ID' },
      { key: 'orderDate', label: 'Order date' },
      { key: 'invoiceNumber', label: 'Invoice number' },
      { key: 'invoiceDate', label: 'Invoice date' },
      { key: 'declaredValue', label: 'Declared value' },
      { key: 'rtoRoutingCode', label: 'RTO routing code' },
    ],
  },
  {
    key: 'shipper',
    title: 'Shipper info',
    fields: [
      { key: 'brandLogo', label: 'Brand logo' },
      { key: 'sellerBrandName', label: 'Seller brand name' },
      { key: 'gstin', label: 'GSTIN' },
      { key: 'shipperAddress', label: 'Shipper address' },
      { key: 'shipperPhone', label: 'Shipper phone' },
      { key: 'rtoAddress', label: 'RTO address' },
      { key: 'rtoContact', label: 'RTO contact number' },
    ],
  },
  {
    key: 'delivery',
    title: 'Delivery info',
    fields: [
      { key: 'deliveryAddress', label: 'Delivery address' },
      { key: 'deliveryContact', label: 'Delivery contact number' },
      { key: 'maskContact', label: 'Mask buyer phone' },
      { key: 'paymentModeHeader', label: 'Payment mode header' },
    ],
  },
  {
    key: 'courier',
    title: 'Courier & shipping',
    fields: [
      { key: 'courierName', label: 'Courier name' },
      { key: 'shippingMode', label: 'Shipping mode' },
      { key: 'serviceType', label: 'Service type' },
      { key: 'deadWeight', label: 'Dead weight' },
      { key: 'dimensions', label: 'Dimensions' },
    ],
  },
  {
    key: 'barcodes',
    title: 'Barcodes & IDs',
    fields: [
      { key: 'orderBarcode', label: 'Order barcode' },
      { key: 'invoiceBarcode', label: 'Invoice barcode' },
      { key: 'trackingNumber', label: 'Tracking number' },
      { key: 'labelPagination', label: 'Label pagination' },
    ],
  },
  {
    key: 'products',
    title: 'Products & package',
    fields: [
      { key: 'itemName', label: 'Item name' },
      { key: 'skuCode', label: 'SKU code' },
      { key: 'productQuantity', label: 'Product quantity' },
      { key: 'productCost', label: 'Product cost' },
      { key: 'otherCharges', label: 'Other charges' },
    ],
  },
];

/**
 * Fields that cannot both be printed.
 *
 * The admin panel warns that "some fields in Invoice and Order cannot be selected
 * simultaneously" without naming them. The one that is certainly true is the barcode: there is a
 * single barcode slot on the label, so an order barcode and an invoice barcode compete for it.
 * TODO(ravi, 2026-09): confirm the full exclusion set with the admin team before development.
 */
export const LABEL_FIELD_EXCLUSIONS = [['orderBarcode', 'invoiceBarcode']];

const LABEL_DEFAULT_ON = [
  'orderId', 'orderDate', 'declaredValue', 'rtoRoutingCode',
  'brandLogo', 'sellerBrandName', 'gstin', 'shipperAddress', 'rtoAddress', 'rtoContact',
  'deliveryAddress', 'deliveryContact', 'maskContact', 'paymentModeHeader',
  'courierName', 'shippingMode', 'serviceType',
  'orderBarcode', 'trackingNumber',
  'itemName', 'skuCode', 'productQuantity',
];

export const DEFAULT_LABEL_CONFIG = {
  printer: 'THERMAL',
  size: '4x6',
  headerText: '',
  footerText: 'Powered by ProShip',
  fields: LABEL_FIELD_GROUPS.reduce((acc, group) => {
    group.fields.forEach((f) => {
      acc[f.key] = LABEL_DEFAULT_ON.includes(f.key);
    });
    return acc;
  }, {}),
};

/** Channels a merchant can override the default label for. */
export const LABEL_CHANNELS = ['SHOPIFY', 'WOOCOMMERCE', 'MAGENTO', 'AMAZON', 'FLIPKART', 'API', 'MANUAL'];

/**
 * Courier attributes — the operating limits ProShip needs before it can allocate to an account.
 *
 * These are not optional in the way serviceability and rates are: allocation cannot decide
 * whether a 12 kg parcel fits an account without knowing its ceiling, and it cannot promise a
 * same-day pickup without the cutoff. They come from the merchant's contract, so they are asked
 * for at connect time rather than defaulted from ProShip.
 */
/*
  NOTHING HERE IS MANDATORY. Every one of these limits has a sensible fallback in allocation, and
  a merchant connecting a courier at 6pm should not be blocked on a volumetric divisor they have to
  go and find. An asterisk on a field the form will submit without is a lie about the form.

  A blank limit means allocation does not enforce that ceiling for this account, which is the same
  position as before the account existed. They can be filled in later from the courier edit drawer.
*/
export const COURIER_META_GROUPS = [
  {
    key: 'weights',
    title: 'Weights',
    fields: [
      { key: 'minChargeableWeight', label: 'Min chargeable wt', unit: 'g', type: 'number', required: false, placeholder: '500' },
      { key: 'minWeight', label: 'Min wt accepted', unit: 'g', type: 'number', required: false, placeholder: '50' },
      { key: 'maxWeightPerPiece', label: 'Max wt / piece', unit: 'g', type: 'number', required: false, placeholder: '30000' },
      { key: 'maxWeightPerShipment', label: 'Max wt / shipment', unit: 'g', type: 'number', required: false, placeholder: '30000' },
      { key: 'volumetricDivisor', label: 'Volumetric divisor', type: 'number', required: false, placeholder: '5000' },
      {
        key: 'chargeableWeightRule',
        label: 'Chargeable weight rule',
        type: 'select',
        required: false,
        options: ['Higher of dead and volumetric', 'Dead weight only', 'Volumetric only'],
      },
    ],
  },
  {
    key: 'dimensions',
    title: 'Dimensions',
    fields: [
      // Most contracts set no lower bound on size, so these start at zero and a merchant only
      // touches them if their carrier actually refuses small parcels.
      { key: 'minLength', label: 'Min length', unit: 'cm', type: 'number', required: false, defaultValue: 0 },
      { key: 'minWidth', label: 'Min width', unit: 'cm', type: 'number', required: false, defaultValue: 0 },
      { key: 'minHeight', label: 'Min height', unit: 'cm', type: 'number', required: false, defaultValue: 0 },
      { key: 'maxLength', label: 'Max length', unit: 'cm', type: 'number', required: false },
      { key: 'maxWidth', label: 'Max width', unit: 'cm', type: 'number', required: false },
      { key: 'maxHeight', label: 'Max height', unit: 'cm', type: 'number', required: false },
      { key: 'maxLwhSum', label: 'Max L+W+H', unit: 'cm', type: 'number', required: false },
    ],
  },
  {
    key: 'pickup',
    title: 'Pickup & delivery',
    fields: [
      { key: 'pickupCutoff', label: 'Pickup cutoff time', type: 'time', required: false, help: 'Last slot for same-day pickup' },
      /*
        MON_SAT and MON_SUN only. WorkingDaysEnum on the courier document has no MON_FRI member,
        so offering it collected a value that was silently dropped on save — the merchant set
        Mon-Fri, the courier stored nothing, and pickup ran all week.
      */
      { key: 'pickupDays', label: 'Pickup days', type: 'select', required: false, options: ['MON_SAT', 'MON_SUN'] },
      { key: 'maxDeliveryAttempts', label: 'Max delivery tries', type: 'number', required: false, placeholder: '3' },
      { key: 'rtoAfterAttempts', label: 'RTO after tries', type: 'number', required: false, placeholder: '3' },
    ],
  },
  {
    key: 'cod',
    title: 'COD',
    fields: [
      { key: 'codSupported', label: 'COD supported', type: 'select', required: false, options: ['Yes', 'No'] },
      { key: 'maxCodValue', label: 'Max COD value', unit: '₹', type: 'number', required: false, placeholder: '50000' },
      { key: 'codRemittanceDays', label: 'COD remittance', unit: 'days', type: 'number', required: false, placeholder: '8' },
    ],
  },
];

/** Flat list, for validation and for the edit form. */
export const COURIER_META_FIELDS = COURIER_META_GROUPS.flatMap((g) => g.fields);

/** Starting values for a new account: whatever a field declares, or blank. */
export const defaultCourierMeta = () =>
  COURIER_META_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: f.defaultValue ?? '' }), {});

/**
 * Where a carrier POSTs status updates.
 *
 * One URL per CARRIER, not per account: every Delhivery contract a merchant holds pushes to the
 * same Delhivery endpoint. The update carries an AWB, ProShip already knows which account issued
 * that AWB, so the account code adds nothing the payload does not already settle — and most
 * carriers only let a client register one callback URL anyway, so a per-account URL would be
 * unconfigurable for exactly the merchants who hold several contracts.
 *
 * TODO(ravi, 2026-09): both the host and the path are unverified. Neither local repo contains the
 * service that receives these — proship-backend is the admin panel, not the API — so this is
 * derived from REACT_APP_API_ENDPOINT as a placeholder. Confirm the real route before a merchant
 * is ever shown this.
 */
export const webhookUrlFor = (carrierName) =>
  `${process.env.REACT_APP_API_ENDPOINT || 'https://proship.prozo.com/api/'}webhook/carrier/${String(
    carrierName || 'carrier'
  )
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')}`;

/** Doorstep and reverse QC checks a merchant can require. */


/**
 * Service types a Direct contract can cover.
 *
 * ProShip nomenclature: this is serviceTypePrimary on the courier catalogue, not a service line.
 * One contract per service — a carrier's air and surface rates, TATs and cutoffs differ, so they
 * are separate accounts rather than one account with a mode.
 */
export const SERVICE_TYPES = ['Surface', 'Air', 'Express', 'Heavy', 'Same Day', 'Next Day', 'Reverse'];

/**
 * Service types whose coverage is a lane, not a pair of lists.
 *
 * Same-day and next-day are sold origin-to-destination: a carrier commits to Delhi → Gurgaon by
 * tonight, and the fact that it also serves Delhi and Bengaluru says nothing about Delhi →
 * Bengaluru same-day. So for these the pickup/drop pair IS the unit of serviceability, and the
 * cross-product of the two columns would promise lanes nobody agreed to.
 */
export const LANE_WISE_SERVICE_TYPES = ['Same Day', 'Next Day'];

export const isLaneWise = (serviceType) => LANE_WISE_SERVICE_TYPES.includes(serviceType);

export const CREDENTIAL_STATE = {
  TESTED: 'TESTED',
  PENDING: 'PENDING',
  FAILED: 'FAILED',
};

/** Allocation strategies a rule can carry. PERSONALIZED is the drag-and-drop order. */
/**
 * The one allocation preference a merchant sets without writing a rule.
 *
 * Rules are powerful and most merchants never want one — they want to say "prefer the cheapest"
 * and be done. This is that setting: the default applied to every order no rule matches, which
 * for a merchant with no rules is every order.
 *
 * `ruleType` IS THE CONTRACT. These are the values ruleengine/setMerchantRule is known to accept,
 * taken from the panel page this replaces. Nothing is offered here that cannot be sent.
 *
 * TWO OF THE FOUR REACH THE ENGINE TODAY. CHEAPEST and PROSHIP_RECOMMENDATION are the only values
 * ruleengine/setMerchantRule is known to accept, so those two save for real. The other two are
 * held locally — an option with no `ruleType` is never posted, because sending an enum the engine
 * rejects is how this screen broke once already.
 *
 * FASTEST has no rule type anywhere in the panel or the engine. It is a real merchant intent and
 * belongs on the screen, but until the engine has a value for it the choice cannot be enacted.
 * TODO(backend): a FASTEST ruleType on setMerchantRule, ranking couriers by TAT on the lane.
 *
 * PERSONALISED IS A COURIER ORDER, NOT A RULE — the merchant names the couriers they want and the
 * sequence to try them in, with no conditions and no rule builder. The engine already models this
 * exactly: a rule whose result is `{type: 'Priority', values: [...couriers]}` (see
 * components/CourierPriority/CustomRule.js). What it lacks is a way to REPLACE one —
 * ruleengine/create_rule creates, with no update or delete beside it, so saving an order twice
 * would leave two rules fighting over the same shipments.
 * TODO(backend): a rule update/upsert endpoint, or a PRIORITY ruleType on setMerchantRule.
 *
 * CUSTOM is read back but never sent: it is the state that results from a custom rule existing,
 * not a value the endpoint takes.
 *
 * `caveat` is a real constraint, not a disclaimer: ProShip does not hold a Direct courier's
 * rates, so cost ranking cannot see an account with no uploaded rate card, and a newly connected
 * account has no performance history to be scored on.
 */
export const COURIER_PRIORITY_OPTIONS = [
  {
    key: 'PACE',
    ruleType: 'PROSHIP_RECOMMENDATION',
    label: 'PACE',
    tagline: 'Let the engine decide',
    blurb: 'Scores every courier per order on performance, cost and speed.',
    pace: true,
    recommended: true,
    caveat: 'A courier needs history with you before it can be scored on its own record. New accounts run on ProShip-wide performance until they build one.',
    note: 'PACE scores each courier per order on your delivery rate, first-attempt success, on-time record, cost and speed. What it weighs most is yours to set — the business knobs live in PACE Engine.',
  },
  {
    key: 'FASTEST',
    // No ruleType: the engine has no value for speed-first allocation yet. See the note above.
    label: 'Fastest',
    tagline: 'Quickest delivery',
    blurb: 'Ranks couriers by how fast they actually deliver the lane, not by what they promise.',
    caveat: 'Speed is usually the most expensive choice — the fastest courier on a lane is rarely the cheapest.',
    note: 'The engine picks the courier with the best transit time on the lane, using delivered history rather than published TAT. Cost is not considered, so freight spend will run higher than any other setting here.',
  },
  {
    key: 'CHEAPEST',
    ruleType: 'CHEAPEST',
    label: 'Cheapest',
    tagline: 'Lowest shipping cost',
    blurb: 'Always the lowest landed cost among couriers that can serve the lane.',
    caveat: 'Your Direct couriers join this only where you have uploaded a rate card — ProShip cannot rank a price it does not hold.',
    note: 'The engine picks the lowest-cost courier partner that can serve the lane, so you spend the least on freight. It does not weigh delivery performance, so a cheap courier with a poor record on a lane will still win it.',
  },
  {
    key: 'PERSONALIZED',
    // No ruleType: CUSTOM cannot be sent, only read back. See the note above.
    reflectsRuleType: 'CUSTOM',
    label: 'Personalised',
    tagline: 'Your own courier order',
    blurb: 'Your list, top to bottom. Allocation tries each in turn until one can take the shipment.',
    custom: true,
    caveat: 'A fixed order ignores cost and performance, so it needs revisiting when a courier’s service changes.',
    note: 'Allocation walks your list from the top and uses the first courier that can serve the lane. Nothing else is considered — which is the point, and the risk.',
  },
];

export const findPriorityOption = (key) =>
  COURIER_PRIORITY_OPTIONS.find((o) => o.key === key) || COURIER_PRIORITY_OPTIONS[0];

/** Strict: returns null rather than a default, so a stale stored key cannot light the wrong card. */
export const priorityOptionByKey = (key) =>
  COURIER_PRIORITY_OPTIONS.find((o) => o.key === key) || null;

/**
 * Back from what the merchant record stores to the option this screen shows.
 *
 * Matches on either the value the card writes or the value it merely reflects, so a stored CUSTOM
 * lights up Personalised even though this screen cannot set it.
 */
export const optionForRuleType = (ruleType) =>
  COURIER_PRIORITY_OPTIONS.find(
    (o) => o.ruleType === ruleType || o.reflectsRuleType === ruleType
  ) || null;

export const PRIORITY_STRATEGIES = [
  {
    key: 'CHEAPEST',
    label: 'Cheapest',
    blurb: 'Lowest landed cost among enabled couriers that can serve the lane',
  },
  {
    key: 'FASTEST',
    label: 'Fastest',
    blurb: 'Shortest committed TAT for the destination zone',
  },
  {
    key: 'PACE',
    label: 'PACE',
    blurb: 'The engine decides per order, on your weighting',
  },
  {
    key: 'PERSONALIZED',
    label: 'Personalised',
    blurb: 'Your own order — drag couriers into priority',
  },
];

/** Rules are never edited or deleted — only switched off. */
export const RULE_STATUS_TABS = [
  { key: 'ACTIVE', label: 'Active' },
  { key: 'INACTIVE', label: 'Inactive' },
  { key: 'ALL', label: 'All' },
];

/**
 * Conditions a rule can match on.
 *
 * Zone values are the panel's own — LOCAL / REGIONAL / METROS / ROI / SPECIAL, as used by
 * Merchant Rule/Rules.js and the shared Filters context. Deliberately NOT the A–F zones in the
 * reference screens: those are another product's taxonomy and would not match any rule the
 * ProShip engine evaluates.
 *
 * `kind` drives which value editor the builder renders:
 *   choice   — one of a fixed set (radio)
 *   multi    — any of a fixed set (checkboxes)
 *   compare  — an operator and a number, with a unit
 *   list     — pasted or CSV-uploaded list of identifiers
 *   text     — free text
 */
export const OPERATORS = [
  { value: 'GTE', label: 'Greater than or equal to (≥)' },
  { value: 'GT', label: 'Greater than (>)' },
  { value: 'LTE', label: 'Less than or equal to (≤)' },
  { value: 'LT', label: 'Less than (<)' },
  { value: 'EQ', label: 'Equal to (=)' },
];

export const OPERATOR_SYMBOL = { GTE: '≥', GT: '>', LTE: '≤', LT: '<', EQ: '=' };

export const RULE_CONDITION_TYPES = [
  {
    key: 'paymentMode',
    label: 'Payment Mode',
    kind: 'choice',
    options: [
      { value: 'PREPAID', label: 'Prepaid' },
      { value: 'COD', label: 'COD' },
    ],
  },
  {
    key: 'zone',
    label: 'Courier Zone',
    kind: 'multi',
    options: [
      { value: 'LOCAL', label: 'Local' },
      { value: 'REGIONAL', label: 'Regional (within state)' },
      { value: 'METROS', label: 'Metro' },
      { value: 'ROI', label: 'Rest of India' },
      { value: 'SPECIAL', label: 'Special' },
    ],
  },
  /*
    KILOGRAMS, not grams. Merchants quote their contracts in kg and think in kg — a slab is
    "0.5 to 1", never "500 to 1000". Asking for grams here meant a rule reading "Weight > 1000"
    that a merchant would read as a tonne. Decimals are allowed, so 0.5 works.
  */
  { key: 'weight', label: 'Weight', kind: 'compare', unit: 'kg', step: '0.01', placeholder: '0.5' },
  { key: 'orderValue', label: 'Order Value', kind: 'compare', unit: '₹' },
  { key: 'quantity', label: 'Product Quantity', kind: 'compare', unit: 'units' },
  { key: 'sku', label: 'Product SKU', kind: 'list', placeholder: 'NM-CLS-42, NM-SCK-BLK' },
  { key: 'pincode', label: 'Delivery Pincode', kind: 'list', placeholder: '110001, 400001' },
  {
    key: 'channel',
    label: 'Channel',
    kind: 'multi',
    options: ['Shopify', 'Woocommerce', 'Magento', 'Amazon', 'Flipkart', 'API', 'Manual'].map((c) => ({
      value: c.toUpperCase(),
      label: c,
    })),
  },
  { key: 'state', label: 'Delivery State', kind: 'text', placeholder: 'Maharashtra, Karnataka' },
  { key: 'city', label: 'Delivery City', kind: 'text', placeholder: 'Mumbai, Bengaluru' },
  { key: 'tag', label: 'Order Tag', kind: 'text', placeholder: 'gift, fragile' },
  /*
    WHEN THE AWB WAS ASSIGNED, as a window rather than a comparison.

    The question behind it is always a shift: "anything assigned after the 6pm cutoff goes to the
    courier that still collects". A single > or < cannot say that — 22:00 to 06:00 crosses
    midnight — so this takes a from and a to, and the matcher handles the wrap.
  */
  {
    key: 'awbAssignedAt',
    label: 'AWB Assignment Time',
    kind: 'between',
    unit: 'time',
  },
];

/**
 * PACE knobs. Held as one set, not one per mode: with modes gone there is nothing to hold a
 * second set against, which is the substantive change from Ashvini's PaceWeighting.
 */


const seedAccount = (id, carrierName, accountCode, serviceType, credentials, config, extra = {}) => ({
  id,
  carrierName,
  /** Key into carrierSchemas — decides which credential fields this account asks for. */
  carrierKey: carrierName.toUpperCase(),
  accountCode,
  serviceType,
  credentials,
  enabled: credentials === CREDENTIAL_STATE.TESTED,
  config,
  /** Courier limits, keyed by COURIER_META_FIELDS. */
  meta: {},
  /** Credential values, keyed by the carrier's own field names. Secrets are never rendered back. */
  credentialValues: {},
  lastTestedAt: null,
  /** Set when the carrier rejects us. Cleared by a passing test. */
  lastError: null,
  ...extra,
});

/**
 * How ProShip prices this merchant's freight.
 *
 * On BLENDED pricing the merchant is quoted one rate across couriers, so courier choice is
 * ProShip's to make — which is why the rules screen is read-only for them. On COURIER_WISE they
 * are quoted per courier and choosing between couriers is theirs.
 *
 * TODO(ravi, 2026-09): this is a merchant-record field, not a client one. Replace with the real
 * read once the backend exposes it; the review control on the Direct home page goes with it.
 */
export const PRICING_MODELS = {
  COURIER_WISE: 'COURIER_WISE',
  BLENDED: 'BLENDED',
};

export const DEFAULT_STATE = {
  pricingModel: PRICING_MODELS.COURIER_WISE,
  plan: {
    active: false,
    planId: null,
    activatedAt: null,
    activatedBy: null,
    activatedVia: null, // SELF or ADMIN
    verifiedVia: null, // address that confirmed the activation OTP
    paidFrom: null, // WALLET or POSTPAID
    autoRenew: true,
    periodStart: null,
    periodEnd: null,
    shipmentsUsed: 0,
    /** Kept for the older per-shipment framing; plans now carry their own overage price. */
    feePerShipment: 1.1,
  },
  ownCouriers: [
    seedAccount('own-1', 'Delhivery', 'ABC_DL_SURFACE_PREPAID', 'Surface', CREDENTIAL_STATE.TESTED, {
      serviceability: 'OWN',
      zones: 'OWN',
      tat: 'OWN',
      rateCard: null,
    }, {
      lastTestedAt: '2026-08-21T11:40:00.000Z',
      meta: {
        minChargeableWeight: 500, maxWeightPerPiece: 30000, maxWeightPerShipment: 30000,
        volumetricDivisor: 5000, chargeableWeightRule: 'Higher of dead and volumetric',
        maxLength: 120, maxWidth: 80, maxHeight: 80, maxLwhSum: 250,
        pickupCutoff: '15:00', pickupDays: 'MON_SAT', maxDeliveryAttempts: 3, rtoAfterAttempts: 3,
        codSupported: 'No',
      },
    }),
    seedAccount('own-2', 'Bluedart', 'ABC_BD_AIR_COD', 'Air', CREDENTIAL_STATE.TESTED, {
      serviceability: 'OWN',
      zones: null,
      tat: null,
      rateCard: null,
    }, {
      lastTestedAt: '2026-08-22T09:05:00.000Z',
      meta: {
        minChargeableWeight: 500, maxWeightPerPiece: 10000, maxWeightPerShipment: 10000,
        volumetricDivisor: 5000, chargeableWeightRule: 'Higher of dead and volumetric',
        maxLength: 90, maxWidth: 60, maxHeight: 60,
        pickupCutoff: '16:30', pickupDays: 'MON_SAT', maxDeliveryAttempts: 3,
        codSupported: 'Yes', maxCodValue: 50000,
      },
    }),
    seedAccount(
      'own-3',
      'Xpressbees',
      'ABC_XB_SURFACE_COD',
      'Surface',
      CREDENTIAL_STATE.FAILED,
      { serviceability: null, zones: null, tat: null, rateCard: null },
      {
        lastTestedAt: '2026-08-25T06:12:00.000Z',
        lastError:
          'HTTP 401 from Xpressbees — the business account exists but the password was rejected. Reset it at the carrier, then update the credentials here.',
      }
    ),
    // A same-day account, so the lane-level branch of serviceability has something to run on.
    // Coverage is deliberately unset: same-day is the case where the file must enumerate every
    // lane, and an already-uploaded account would hide the screen that explains that.
    seedAccount(
      'own-4',
      'Ekart',
      'ABC_EK_SAMEDAY_BLR',
      'Same Day',
      CREDENTIAL_STATE.TESTED,
      { serviceability: null, zones: null, tat: 'OWN', rateCard: null },
      {
        lastTestedAt: '2026-08-26T05:30:00.000Z',
        meta: {
          minChargeableWeight: 500, maxWeightPerPiece: 5000, maxWeightPerShipment: 5000,
          volumetricDivisor: 5000, chargeableWeightRule: 'Higher of dead and volumetric',
          minLength: 0, minWidth: 0, minHeight: 0,
          maxLength: 45, maxWidth: 45, maxHeight: 45,
          pickupCutoff: '11:00', pickupDays: 'MON_SUN', maxDeliveryAttempts: 2,
          codSupported: 'No',
        },
      }
    ),
  ],
  /** Courier codes the merchant has switched off. Empty means every ProShip courier is usable. */
  disabledProzo: [],
  /**
   * The merchant's own courier order, plus whichever preference the merchant record cannot hold.
   *
   * `chosen` is the key of the active local option (FASTEST or PERSONALIZED) or null when the
   * live preference is one the engine stores itself. It is a key rather than a flag because there
   * is more than one option the engine has no value for — see COURIER_PRIORITY_OPTIONS.
   */
  courierPriority: { chosen: null, order: [], savedAt: null },
  /**
   * Branded tracking page. RETAINED AS A KEY ONLY: the screen that wrote it has been replaced by
   * /tracking-config, which persists to the merchant record instead. Left in the shape so an
   * older stored blob carrying it does not look like corruption on read.
   * Nothing writes it any more.
   */
  trackingPage: null,
  /**
   * Rate cards, keyed by account id:
   * {[accountId]: {slabs: [{slab, rates: [5]}], summary, fileName, uploadedAt, uploadedBy}}
   *
   * In production this is the one slice that would never live in the browser — see the encryption
   * note on the Rate Card page. It is here because there is no endpoint yet.
   */
  rateCards: {},
  /**
   * Coverage files, keyed by account id:
   * {[accountId]: {rows: [{pickupPincode, dropPincode}], summary, uploadedAt, uploadedBy}}
   */
  serviceabilityLanes: {},
  /**
   * Pincode-level zone maps, keyed by account id:
   * {[accountId]: {rows: [{pickupPincode, deliveryPincode, zone}], summary, uploadedAt, uploadedBy}}
   *
   * Separate from ownCouriers.config.zones, which records only whether the merchant supplied a
   * zone map at all. This holds the lanes themselves.
   */
  zoneMaps: {},
  /** Blocked pincodes per courier: {[code]: {firstMile: [], lastMile: [], cod: [], rvp: []}} */
  blocked: {},
  /** Audit of every block/unblock upload, newest first. */
  uploadHistory: [],
  /** Who switched what on or off, and when. Newest first. */
  activity: [],
  /**
   * Label config: one default, plus per-channel overrides. A channel with no entry here inherits
   * the default, which is different from holding an identical copy — change the default and every
   * inheriting channel follows.
   */
  labelConfig: { default: DEFAULT_LABEL_CONFIG, channels: {} },
  /**
   * Returns and doorstep QC — one policy for every reverse pickup on every courier.
   * Shape and seed come from data/rvpQc.js, which mirrors the admin module; null means
   * "not yet touched by this merchant" and reads through to that seed.
   */
  returnsQc: null,
  /**
   * A condition is {type, operator?, value} so one rule can hold two conditions on the same
   * field (order value between two bounds), which a keyed object could not express.
   * Rules are append-only: created, cloned, switched off — never edited or deleted.
   */
  rules: [
    {
      id: 'rule-1',
      name: 'COD heavy to metros',
      conditions: [
        { type: 'paymentMode', value: 'COD' },
        { type: 'weight', operator: 'GTE', value: '1000' },
        { type: 'zone', value: ['METROS'] },
      ],
      strategy: 'PERSONALIZED',
      order: ['direct:ABC_DL_SURFACE_PREPAID'],
      enabled: true,
      fallback: true,
      createdBy: 'ops@prozo.com',
      createdOn: '2026-08-18T09:24:00.000Z',
      lastModified: '2026-08-18T09:24:00.000Z',
    },
    {
      id: 'rule-2',
      name: 'High value prepaid',
      conditions: [
        { type: 'paymentMode', value: 'PREPAID' },
        { type: 'orderValue', operator: 'GTE', value: '5000' },
      ],
      strategy: 'FASTEST',
      order: [],
      enabled: true,
      fallback: true,
      createdBy: 'ops@prozo.com',
      createdOn: '2026-08-20T14:02:00.000Z',
      lastModified: '2026-08-20T14:02:00.000Z',
    },
    {
      id: 'rule-3',
      name: 'Everything else',
      conditions: [],
      strategy: 'PACE',
      order: [],
      enabled: true,
      fallback: false,
      createdBy: 'ops@prozo.com',
      createdOn: '2026-08-20T14:05:00.000Z',
      lastModified: '2026-08-20T14:05:00.000Z',
    },
  ],
  /**
   * PACE, in the shape the real API uses: a sparse map of merchant overrides on top of ProShip's
   * global defaults, plus the allocation profile and the audit trail every change requires.
   * Anything absent from `overrides` inherits the global default — see data/paceKnobs.js.
   */
  pace: {
    profile: 'weighted',
    overrides: {},
    /** Newest first: {key, from, to, reason, by, at}. */
    history: [],
  },
};

